/**
 * AI Documentation Template Selector
 * 
 * Allows users to select and generate pre-populated documentation templates
 * based on visit type and discipline.
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText,
  Sparkles,
  Clock,
  Copy,
  Check,
  Loader2,
  ChevronRight,
  Stethoscope,
  Heart,
  Users,
  BookHeart,
  Zap,
  FileSignature
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Discipline icons
const DISCIPLINE_ICONS = {
  skilled_nurse: Stethoscope,
  aide: Heart,
  social_work: Users,
  chaplain: BookHeart
};

// Visit type icons
const VISIT_TYPE_ICONS = {
  routine_visit: FileText,
  admission_visit: FileSignature,
  prn_visit: Zap,
  death_visit: Heart,
  bereavement_visit: BookHeart
};

export default function DocumentationTemplateSelector({
  onTemplateSelect,
  patientContext = null,
  defaultDiscipline = "skilled_nurse",
  compact = false
}) {
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(defaultDiscipline);
  const [selectedVisitType, setSelectedVisitType] = useState("");
  const [template, setTemplate] = useState(null);
  const [quickTemplates, setQuickTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("hospice_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch available templates
  useEffect(() => {
    fetchTemplates();
    fetchQuickTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/documentation-templates/list`,
        { headers: getAuthHeaders() }
      );
      setDisciplines(response.data.disciplines || []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      // Fallback data
      setDisciplines([
        {
          discipline: "skilled_nurse",
          display_name: "Skilled Nurse",
          templates: [
            { visit_type: "routine_visit", name: "Routine Skilled Nursing Visit", section_count: 10 },
            { visit_type: "admission_visit", name: "Admission/Start of Care Visit", section_count: 9 },
            { visit_type: "prn_visit", name: "PRN/Unscheduled Visit", section_count: 6 },
            { visit_type: "death_visit", name: "Death Pronouncement Visit", section_count: 6 }
          ]
        },
        {
          discipline: "aide",
          display_name: "Aide/HHA",
          templates: [
            { visit_type: "routine_visit", name: "Routine Aide Visit", section_count: 7 }
          ]
        },
        {
          discipline: "social_work",
          display_name: "Social Worker",
          templates: [
            { visit_type: "routine_visit", name: "Social Work Visit", section_count: 7 },
            { visit_type: "bereavement_visit", name: "Bereavement Visit", section_count: 5 }
          ]
        },
        {
          discipline: "chaplain",
          display_name: "Chaplain",
          templates: [
            { visit_type: "routine_visit", name: "Chaplain/Spiritual Care Visit", section_count: 6 }
          ]
        }
      ]);
    }
  };

  const fetchQuickTemplates = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/documentation-templates/quick-templates`,
        { headers: getAuthHeaders() }
      );
      setQuickTemplates(response.data.quick_templates || []);
    } catch (err) {
      console.error("Failed to fetch quick templates:", err);
      // Fallback
      setQuickTemplates([
        { id: "stable_visit", name: "Stable Visit Summary", content: "Patient visited today. Condition stable. Vital signs within normal limits. Pain controlled. Medications reviewed, no changes. Continue current plan of care." },
        { id: "pain_increase", name: "Pain Increase Assessment", content: "Patient reports increased pain. PRN medication administered. MD notified. Will reassess at next visit." },
        { id: "symptom_management", name: "Symptom Management Note", content: "Patient experiencing symptoms. Assessment completed. Interventions provided. Patient response documented." }
      ]);
    }
  };

  const generateTemplate = async () => {
    if (!selectedDiscipline || !selectedVisitType) {
      toast.error("Please select discipline and visit type");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/documentation-templates/generate`,
        {
          discipline: selectedDiscipline,
          visit_type: selectedVisitType,
          patient_context: patientContext,
          include_placeholders: true
        },
        { headers: getAuthHeaders() }
      );
      
      setTemplate(response.data);
      setDialogOpen(true);
    } catch (err) {
      console.error("Failed to generate template:", err);
      toast.error("Failed to generate template");
    } finally {
      setLoading(false);
    }
  };

  const useTemplate = () => {
    if (!template) return;
    
    // Combine all sections into one document
    const fullContent = template.sections
      .map(s => `**${s.title}**\n${s.content}`)
      .join("\n\n");
    
    if (onTemplateSelect) {
      onTemplateSelect(fullContent, template);
    }
    
    setDialogOpen(false);
    toast.success("Template applied to documentation");
  };

  const copyTemplate = () => {
    if (!template) return;
    
    const fullContent = template.sections
      .map(s => `${s.title}\n${s.content}`)
      .join("\n\n");
    
    navigator.clipboard.writeText(fullContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Template copied to clipboard");
  };

  const applyQuickTemplate = (quickTemplate) => {
    if (onTemplateSelect) {
      onTemplateSelect(quickTemplate.content, { name: quickTemplate.name });
    }
    toast.success(`${quickTemplate.name} inserted`);
  };

  const currentDiscipline = disciplines.find(d => d.discipline === selectedDiscipline);
  const DisciplineIcon = DISCIPLINE_ICONS[selectedDiscipline] || FileText;

  if (compact) {
    return (
      <div className="space-y-3" data-testid="template-selector-compact">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Sparkles className="w-3 h-3 text-violet-400" />
          Quick Templates
        </div>
        <div className="flex flex-wrap gap-1.5">
          {quickTemplates.slice(0, 3).map((qt) => (
            <Button
              key={qt.id}
              variant="outline"
              size="sm"
              className="text-xs h-7 bg-slate-800/50 border-slate-700 hover:bg-slate-700"
              onClick={() => applyQuickTemplate(qt)}
            >
              {qt.name}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="template-selector">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-400" />
          AI Documentation Templates
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Discipline Selection */}
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Discipline</label>
          <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
            <SelectTrigger className="bg-slate-800 border-slate-700">
              <SelectValue placeholder="Select discipline" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {disciplines.map((d) => {
                const Icon = DISCIPLINE_ICONS[d.discipline] || FileText;
                return (
                  <SelectItem key={d.discipline} value={d.discipline}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-slate-400" />
                      {d.display_name}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Visit Type Selection */}
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Visit Type</label>
          <Select 
            value={selectedVisitType} 
            onValueChange={setSelectedVisitType}
            disabled={!currentDiscipline}
          >
            <SelectTrigger className="bg-slate-800 border-slate-700">
              <SelectValue placeholder="Select visit type" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {currentDiscipline?.templates.map((t) => {
                const Icon = VISIT_TYPE_ICONS[t.visit_type] || FileText;
                return (
                  <SelectItem key={t.visit_type} value={t.visit_type}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-slate-400" />
                      {t.name}
                      <Badge variant="outline" className="ml-2 text-xs">
                        {t.section_count} sections
                      </Badge>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Generate Button */}
        <Button
          className="w-full bg-violet-600 hover:bg-violet-700"
          onClick={generateTemplate}
          disabled={!selectedDiscipline || !selectedVisitType || loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Template
            </>
          )}
        </Button>

        <Separator className="bg-slate-700" />

        {/* Quick Templates */}
        <div>
          <p className="text-xs text-slate-400 mb-2">Quick Templates</p>
          <div className="space-y-1.5">
            {quickTemplates.map((qt) => (
              <Button
                key={qt.id}
                variant="ghost"
                className="w-full justify-start text-sm h-8 text-slate-300 hover:text-white hover:bg-slate-700"
                onClick={() => applyQuickTemplate(qt)}
              >
                <Zap className="w-3 h-3 mr-2 text-amber-400" />
                {qt.name}
                <ChevronRight className="w-3 h-3 ml-auto text-slate-500" />
              </Button>
            ))}
          </div>
        </div>
      </CardContent>

      {/* Template Preview Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DisciplineIcon className="w-5 h-5 text-violet-400" />
              {template?.template_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {template?.description}
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Est. {template?.estimated_completion_time}
                </span>
                <Badge variant="outline" className="text-xs">
                  {template?.sections?.length} sections
                </Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] pr-4">
            <div className="space-y-4">
              {template?.sections?.map((section, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="font-medium text-teal-400 mb-2">{section.title}</h4>
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans">
                    {section.content}
                  </pre>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={copyTemplate}>
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={useTemplate}>
              <FileText className="w-4 h-4 mr-2" />
              Use Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export { DocumentationTemplateSelector };
